import { useEffect, useState } from "react";
import { BellRing, Plus, Pencil, Trash2, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useStatuses } from "@/context/StatusContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReminderForm } from "@/components/ReminderForm";

const EMPTY = { name: "", enabled: true, area: "ticket", statuses: [], categories: [], aging_op: "gte", aging_days: 2, time: "08:00", recipients: [], subject: "", body: "" };
const LAST = { sent: "inviata", skipped: "nessun ticket, non inviata", failed: "invio non riuscito" };

export default function ReminderSettings() {
  const { options: statusOptions, labelOf } = useStatuses();
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/mail-reminders").then((r) => setRules(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data.map((c) => c.name))).catch(() => {}); }, []);

  const payload = (f) => { const { id, created_at, created_by, last_run, last_run_date, ...p } = f; return p; };
  const save = async () => {
    setBusy("save");
    try {
      if (form.id) await api.put(`/mail-reminders/${form.id}`, payload(form)); else await api.post("/mail-reminders", payload(form));
      toast.success("Promemoria salvato"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    try { const { data } = await api.post("/mail-reminders/preview", payload(form)); setPreview(data); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (r, enabled) => { try { await api.put(`/mail-reminders/${r.id}`, payload({ ...r, enabled })); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (r) => { if (!window.confirm(`Eliminare il promemoria "${r.name}"?`)) return; await api.delete(`/mail-reminders/${r.id}`); load(); };
  const sendNow = async (r) => {
    setBusy(r.id);
    try { const { data } = await api.post(`/mail-reminders/${r.id}/send-now`); data.status === "sent" ? toast.success(`Mail inviata con ${data.count} ticket`) : toast.info(data.error); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const describe = (r) => [
    r.area === "manutenzione" ? "Manutenzione" : r.area === "ticket" ? "Delivery & Assurance" : "Tutte le sezioni",
    r.statuses.length ? r.statuses.map(labelOf).join(", ") : "tutti gli stati",
    r.categories.length ? r.categories.join(", ") : null,
    `aging ${r.aging_op === "eq" ? "=" : "≥"} ${r.aging_days} gg`, `ogni giorno alle ${r.time}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="reminder-settings">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><BellRing size={18} className="text-primary" /><h3 className="font-bold text-navy">Promemoria via mail</h3></div>
        <Button size="sm" onClick={() => { setPreview(null); setForm({ ...EMPTY }); }} data-testid="rem-new"><Plus size={15} />Nuovo promemoria</Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Mail automatiche con l'elenco dei ticket che rispettano le condizioni. Se nessun ticket corrisponde, la mail non parte.</p>
      {rules.length === 0 ? <p className="text-sm text-muted-foreground">Nessun promemoria configurato.</p> : (
        <div className="space-y-2">
          {rules.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3" data-testid={`rem-row-${r.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-navy">{r.name}</p>
                <p className="text-xs text-muted-foreground">{describe(r)}</p>
                <p className="text-xs text-muted-foreground">A: {r.recipients.join(", ")}{r.last_run ? ` · Ultimo invio ${new Date(r.last_run.at).toLocaleString("it-IT")}: ${LAST[r.last_run.status] || r.last_run.status}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={r.enabled} onCheckedChange={(v) => toggle(r, v)} data-testid={`rem-toggle-${r.id}`} />
                <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => sendNow(r)} data-testid={`rem-send-${r.id}`}><Send size={14} />Invia ora</Button>
                <Button size="sm" variant="outline" onClick={() => { setPreview(null); setForm({ ...r }); }} data-testid={`rem-edit-${r.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`rem-del-${r.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? "Modifica promemoria" : "Nuovo promemoria"}</DialogTitle></DialogHeader>
          {form && <ReminderForm form={form} setForm={setForm} statusOptions={statusOptions} categories={categories} />}
          {preview && (
            <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs" data-testid="rem-preview">
              <p className="mb-1 font-semibold">{preview.count} ticket corrispondono oggi</p>
              {preview.items.slice(0, 10).map((t) => <p key={t.id}>{t.code} · {t.client_name || "—"} · {labelOf(t.status)} · {t.aging} gg</p>)}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={doPreview} data-testid="rem-preview-btn"><Eye size={15} />Anteprima ticket</Button>
            <Button onClick={save} disabled={busy === "save"} data-testid="rem-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
